import { Add, Clear } from "@mui/icons-material";
import {
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  styled,
  TextField,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ValidationContext,
  ValidationTextField,
} from "@components/ValidationContext/ValidationContext";

import type { DnsConfig, DnsPort } from "../utils";
import { DNS_NAME_MAX_LENGTH, isReplicaJob, validateDnsName } from "../utils";
import { AddRowButton, wideFieldStyle } from "./NewJobFormLayout";

/**
 * Bottom padding matches CommandField and Resources rows so this section's
 * trailing space lines up with other steps' last children.
 */
const DnsSection = styled(Stack)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
}));

/**
 * Grid for a single port row. Port values are short and fixed-width
 * (5-digit port, 3-char protocol), so fields use fixed widths rather than
 * the form's `var(--col)` pattern. Left-justified and compact at any width.
 */
const PortRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "120px 120px 120px auto",
  columnGap: theme.spacing(1),
  alignItems: "start",
  // Without `justifyContent: start` the trailing `auto` column expands to
  // fill the parent's width and the clear IconButton stretches with it.
  justifyContent: "start",
  "& .clear": { marginTop: 6 },
}));

type Props = {
  value: DnsConfig | undefined;
  onUpdate: (config: DnsConfig | undefined) => void;
  /** Used to build the `http://dns-<accountId>-` adornment on single-job
   * names so the user can read (and select+copy) the full URL. Ignored when
   * the job is a replica — those use a generated group ID + rank suffix
   * instead. */
  accountId: string | null;
  /** Replica count from `jobSpec.resources.replicas`. When > 1, the input
   * shows generated placeholders for the group ID and per-replica rank. */
  replicas: number | undefined;
};

let nextRowKey = 0;
function getUniqueRowKey() {
  return nextRowKey++;
}

function InternalDnsField({ value, onUpdate, accountId, replicas }: Props) {
  const isReplica = isReplicaJob(replicas);
  // Backend prepends differently for replica jobs: `dns-<groupId>-<name>-<rank>`
  // (groupId is generated at submit, rank varies 0..N-1). For single jobs:
  // `dns-<accountId>-<name>` (account known from current selection).
  const prefix = isReplica
    ? "http://dns-<group-id>-"
    : accountId
      ? `http://dns-${accountId}-`
      : null;
  const suffix = isReplica ? "-<rank>" : null;
  const config = useMemo(() => value ?? { name: "", ports: [] }, [value]);

  const [rowKeys, setRowKeys] = useState<number[]>(() =>
    config.ports.map(getUniqueRowKey),
  );

  // Emit `undefined` when name and ports are both empty. Backend requires
  // both whenever the section is present, so "absent" === "both empty".
  const emit = useCallback(
    (next: DnsConfig) => {
      onUpdate(!next.name && next.ports.length === 0 ? undefined : next);
    },
    [onUpdate],
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      emit({ ...config, name: e.target.value });
    },
    [config, emit],
  );

  const handlePortChange = useCallback(
    (index: number, field: keyof DnsPort, newValue: string) => {
      const updatedPorts = config.ports.map((p, i) => {
        if (i !== index) return p;
        if (field === "protocol") {
          return { ...p, protocol: (newValue as "TCP" | "UDP") || undefined };
        }
        // Store "" for empty/invalid input so half-filled rows survive mid-edit
        // (see DnsPort type). Non-integers (NaN, 12.5) are coerced to "" too.
        const parsed = Number(newValue);
        const numeric =
          newValue === "" || !Number.isInteger(parsed) ? "" : parsed;
        return { ...p, [field]: numeric };
      });
      emit({ ...config, ports: updatedPorts });
    },
    [config, emit],
  );

  const handleAddPort = useCallback(() => {
    setRowKeys((keys) => keys.concat(getUniqueRowKey()));
    emit({
      ...config,
      ports: config.ports.concat({ port: "", targetPort: "" }),
    });
  }, [config, emit]);

  const handleRemovePort = useCallback(
    (index: number) => {
      setRowKeys((keys) => keys.filter((_, i) => i !== index));
      emit({ ...config, ports: config.ports.filter((_, i) => i !== index) });
    },
    [config, emit],
  );

  // Active iff name or ports is non-empty — when active, backend requires both.
  const sectionActive = Boolean(config.name) || config.ports.length > 0;

  const nameError =
    validateDnsName(config.name) ??
    (sectionActive && !config.name ? "DNS name is required" : null);

  const portErrors = useMemo(() => {
    const seen = new Map<number, number>();
    return config.ports.map((p, i) => {
      const errors: { port?: string; targetPort?: string } = {};
      if (p.port === "" || p.port === undefined) {
        errors.port = "Required";
      } else if (!Number.isInteger(p.port) || p.port < 1 || p.port > 65535) {
        errors.port = "Must be 1–65535";
      } else if (seen.has(p.port)) {
        // Backend treats 80/TCP and 80/UDP as the same port for uniqueness.
        errors.port = "Duplicate port";
      } else {
        seen.set(p.port, i);
      }
      if (p.targetPort !== undefined && p.targetPort !== "") {
        if (
          !Number.isInteger(p.targetPort) ||
          p.targetPort < 1 ||
          p.targetPort > 65535
        ) {
          errors.targetPort = "Must be 1–65535";
        }
      }
      return errors;
    });
  }, [config.ports]);

  // "≥1 port required" has no field to attach to when ports.length === 0;
  // register a synthetic instanceId so submit/validate treat it like a field.
  const ADD_PORT_INSTANCE_ID = "internal-dns-add-port";
  const addPortError =
    sectionActive && config.ports.length === 0 ? "Add at least one port" : null;
  const { errors: contextErrors, setErrorInfo } = useContext(ValidationContext);
  useEffect(() => {
    setErrorInfo(
      ADD_PORT_INSTANCE_ID,
      addPortError ? { visible: false } : null,
    );
  }, [addPortError, setErrorInfo]);
  useEffect(() => {
    return () => setErrorInfo(ADD_PORT_INSTANCE_ID, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const addPortErrorVisible =
    addPortError && contextErrors.get(ADD_PORT_INSTANCE_ID)?.visible
      ? addPortError
      : null;

  return (
    <DnsSection spacing={3}>
      <ValidationTextField
        instanceId="internal-dns-name"
        valueForValidation={config.name}
        variant="outlined"
        fullWidth
        label="DNS name"
        placeholder="e.g. my-service"
        InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: DNS_NAME_MAX_LENGTH }}
        InputProps={
          prefix || suffix
            ? {
                startAdornment: prefix ? (
                  <InputAdornment
                    position="start"
                    disableTypography
                    sx={{ mr: 0 }}
                  >
                    {prefix}
                  </InputAdornment>
                ) : undefined,
                endAdornment: suffix ? (
                  <InputAdornment
                    position="end"
                    disableTypography
                    sx={{ ml: 0 }}
                  >
                    {suffix}
                  </InputAdornment>
                ) : undefined,
              }
            : undefined
        }
        value={config.name}
        onChange={handleNameChange}
        error={nameError}
        helperText={
          nameError ||
          `1–${DNS_NAME_MAX_LENGTH} chars: lowercase letters, numbers, dashes (must start with a letter; no trailing dash).${
            isReplica
              ? " <group-id> is assigned at submit; <rank> varies from 0 to replicas-1."
              : accountId
                ? ""
                : " The dns-<account-id>- prefix is added automatically once an account is selected."
          }`
        }
        sx={wideFieldStyle}
      />

      <Stack spacing={0}>
        {config.ports.length > 0 && (
          <Stack spacing={0}>
            {config.ports.map((port, index) => (
              <PortRow key={rowKeys[index]}>
                <ValidationTextField
                  className="port"
                  instanceId={`internal-dns-port-${rowKeys[index]}`}
                  valueForValidation={port.port}
                  variant="outlined"
                  fullWidth
                  label="Port"
                  type="number"
                  required
                  inputProps={{ min: 1, max: 65535, step: 1 }}
                  value={port.port}
                  onChange={(e) =>
                    handlePortChange(index, "port", e.target.value)
                  }
                  error={portErrors[index]?.port ?? null}
                  helperText={portErrors[index]?.port ?? " "}
                  size="small"
                />
                <TextField
                  className="targetPort"
                  variant="outlined"
                  fullWidth
                  label="Target port"
                  type="number"
                  inputProps={{ min: 1, max: 65535, step: 1 }}
                  placeholder="same as port"
                  InputLabelProps={{ shrink: true }}
                  value={port.targetPort ?? ""}
                  onChange={(e) =>
                    handlePortChange(index, "targetPort", e.target.value)
                  }
                  error={Boolean(portErrors[index]?.targetPort)}
                  helperText={portErrors[index]?.targetPort ?? " "}
                  size="small"
                />
                <TextField
                  className="protocol"
                  variant="outlined"
                  fullWidth
                  select
                  label="Protocol"
                  value={port.protocol ?? "TCP"}
                  onChange={(e) =>
                    handlePortChange(index, "protocol", e.target.value)
                  }
                  size="small"
                  helperText=" "
                >
                  <MenuItem value="TCP">TCP</MenuItem>
                  <MenuItem value="UDP">UDP</MenuItem>
                </TextField>
                <IconButton
                  className="clear"
                  onClick={() => handleRemovePort(index)}
                  title="Remove port"
                >
                  <Clear />
                </IconButton>
              </PortRow>
            ))}
          </Stack>
        )}

        <AddRowButton
          color="primary"
          onClick={handleAddPort}
          error={addPortErrorVisible}
        >
          <Add /> Add port
        </AddRowButton>
      </Stack>
    </DnsSection>
  );
}

export default React.memo(InternalDnsField);
